import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ReplyIcon from "@mui/icons-material/Reply";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  addDoc,
  collection,
  deleteField,
  doc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import { getProfilePathByUid } from "../../utils/publicProfile";
import { ellipsizeProfileText } from "../../utils/profileText";

function getInitials(value) {
  if (!value || typeof value !== "string") return "U";
  const parts = value.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "U";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function resolveUserLabel(userRecord) {
  const displayName =
    typeof userRecord?.displayName === "string" ? userRecord.displayName.trim() : "";
  if (displayName) return ellipsizeProfileText(displayName);

  const email = typeof userRecord?.email === "string" ? userRecord.email.trim() : "";
  if (email) return ellipsizeProfileText(email);

  return "Connection";
}

function normalizeHandle(value) {
  const raw = String(value || "").trim().replace(/^@+/, "");
  if (!raw) return "";
  return raw.replace(/[^a-zA-Z0-9._]/g, "").toLowerCase();
}

function deriveFallbackHandle(userRecord, commentNode) {
  const emailCandidate = String(userRecord?.email || commentNode?.authorEmail || "").trim();
  if (emailCandidate.includes("@")) {
    const localPart = normalizeHandle(emailCandidate.split("@")[0]);
    if (localPart) return localPart;
  }

  const displayCandidate = String(
    userRecord?.displayName || commentNode?.authorDisplayName || ""
  ).trim();
  if (displayCandidate) {
    const compact = normalizeHandle(displayCandidate.replace(/\s+/g, "_"));
    if (compact) return compact;
  }

  return "";
}

function resolvePublicHandle(userRecord, commentNode) {
  const direct = normalizeHandle(
    userRecord?.publicHandle ||
      userRecord?.userName ||
      commentNode?.authorPublicHandle ||
      commentNode?.authorUserName ||
      ""
  );
  if (direct) return direct;
  return deriveFallbackHandle(userRecord, commentNode);
}

function formatTimestamp(ts) {
  if (!ts) return "just now";
  const asDate = typeof ts?.toDate === "function" ? ts.toDate() : null;
  if (!asDate || Number.isNaN(asDate.getTime())) return "just now";
  const diffMs = Date.now() - asDate.getTime();
  if (diffMs < 60 * 1000) return "now";
  const mins = Math.floor(diffMs / (60 * 1000));
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return asDate.toLocaleDateString();
}

function getMentionContext(text, caretPosition) {
  if (typeof text !== "string" || !Number.isFinite(caretPosition)) return null;
  const prefix = text.slice(0, Math.max(0, caretPosition));
  const match = prefix.match(/(^|\s)@([a-zA-Z0-9._]{0,30})$/);
  if (!match) return null;

  const mentionText = match[0] || "";
  const query = match[2] || "";
  const start = prefix.length - mentionText.length + (mentionText.startsWith(" ") ? 1 : 0);
  const end = prefix.length;
  if (start < 0 || end < start) return null;

  return {
    query: normalizeHandle(query),
    start,
    end,
  };
}

function extractMentionHandles(text) {
  const source = String(text || "");
  const handles = new Set();
  const regex = /(^|\s)@([a-zA-Z0-9._]{1,30})/g;
  let match = regex.exec(source);
  while (match) {
    const candidate = normalizeHandle(match[2]);
    if (candidate) handles.add(candidate);
    match = regex.exec(source);
  }
  return Array.from(handles);
}

const COMMENT_LIMIT = 500;
const MAX_NESTING_DEPTH = 2;
const HIDE_SCORE_THRESHOLD = -5;

export function getPrivateMentionParticipantIds(capsule) {
  const ids = new Set();
  const senderId = String(capsule?.senderId || "").trim();
  if (senderId) ids.add(senderId);

  const recipients = Array.isArray(capsule?.recipientUserIds)
    ? capsule.recipientUserIds
    : [];
  recipients.forEach((id) => {
    const uid = String(id || "").trim();
    if (uid) ids.add(uid);
  });

  const claimants = Array.isArray(capsule?.claimedBy) ? capsule.claimedBy : [];
  claimants.forEach((id) => {
    const uid = String(id || "").trim();
    if (uid) ids.add(uid);
  });

  return ids;
}

export function isMentionableUserId(uid, isPrivateCapsule, privateMentionParticipantIds) {
  const normalizedUid = String(uid || "").trim();
  if (!normalizedUid) return false;
  if (!isPrivateCapsule) return true;
  return privateMentionParticipantIds instanceof Set && privateMentionParticipantIds.has(normalizedUid);
}

function buildRawDepthById(list) {
  const byId = new Map();
  const memo = new Map();

  list.forEach((comment) => {
    if (comment?.id) byId.set(comment.id, comment);
  });

  const getDepth = (id, trail = new Set()) => {
    if (!id || !byId.has(id)) return 0;
    if (memo.has(id)) return memo.get(id);
    if (trail.has(id)) return 0;

    const nextTrail = new Set(trail);
    nextTrail.add(id);

    const node = byId.get(id);
    const parentId = node?.parentId || null;
    if (!parentId || !byId.has(parentId)) {
      memo.set(id, 0);
      return 0;
    }

    const depth = getDepth(parentId, nextTrail) + 1;
    memo.set(id, depth);
    return depth;
  };

  list.forEach((comment) => {
    if (comment?.id) getDepth(comment.id);
  });

  return memo;
}

function buildCommentTree(list, rawDepthById, maxDepth = MAX_NESTING_DEPTH) {
  const byId = new Map();
  const roots = [];

  list.forEach((comment) => {
    if (comment?.id) byId.set(comment.id, { ...comment, children: [] });
  });

  const resolveCappedParentId = (comment) => {
    const originalParentId = comment?.parentId || null;
    if (!originalParentId || !byId.has(originalParentId)) return null;

    const nodeDepth = Number(rawDepthById.get(comment.id));
    if (!Number.isFinite(nodeDepth) || nodeDepth <= maxDepth) return originalParentId;

    let cursorId = originalParentId;
    while (cursorId && byId.has(cursorId)) {
      const cursorDepth = Number(rawDepthById.get(cursorId));
      if (Number.isFinite(cursorDepth) && cursorDepth === maxDepth - 1) {
        return cursorId;
      }
      cursorId = byId.get(cursorId)?.parentId || null;
    }

    return originalParentId;
  };

  list.forEach((comment) => {
    const node = byId.get(comment.id);
    if (!node) return;

    const cappedParentId = resolveCappedParentId(comment);
    if (!cappedParentId || !byId.has(cappedParentId)) {
      roots.push(node);
      return;
    }
    byId.get(cappedParentId).children.push(node);
  });

  return roots;
}

export function CapsuleThread({ capsuleId, capsule, currentUserId, usersList = [] }) {
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down("sm"));
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newCommentText, setNewCommentText] = useState("");
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replyOpenById, setReplyOpenById] = useState({});
  const [collapsedById, setCollapsedById] = useState({});
  const [postingTopLevel, setPostingTopLevel] = useState(false);
  const [postingReplyById, setPostingReplyById] = useState({});
  const [editingById, setEditingById] = useState({});
  const [editDrafts, setEditDrafts] = useState({});
  const [savingEditById, setSavingEditById] = useState({});
  const [deletingById, setDeletingById] = useState({});
  const [deleteTargetNode, setDeleteTargetNode] = useState(null);
  const [votingById, setVotingById] = useState({});
  const [showHiddenById, setShowHiddenById] = useState({});
  const [mentionContext, setMentionContext] = useState(null);
  const [keyboardInset, setKeyboardInset] = useState(0);
  const topLevelInputRef = useRef(null);
  const replyInputRefs = useRef({});

  const allowedParticipantIds = useMemo(() => {
    const ids = new Set();
    const senderId = capsule?.senderId || "";
    if (senderId) ids.add(senderId);
    const recipients = Array.isArray(capsule?.recipientUserIds)
      ? capsule.recipientUserIds.filter(Boolean)
      : [];
    recipients.forEach((id) => ids.add(id));
    const claimants = Array.isArray(capsule?.claimedBy)
      ? capsule.claimedBy.filter(Boolean)
      : [];
    claimants.forEach((id) => ids.add(id));
    return ids;
  }, [capsule]);

  const capsuleType = String(capsule?.capsuleType || "").trim().toLowerCase();
  const isPrivateCapsule = capsuleType === "private";
  const canAccessThread = isPrivateCapsule
    ? Boolean(currentUserId && allowedParticipantIds.has(currentUserId))
    : true;
  const canPostToThread = Boolean(currentUserId && canAccessThread);
  const privateMentionParticipantIds = useMemo(
    () => getPrivateMentionParticipantIds(capsule),
    [capsule]
  );

  const userById = useMemo(() => {
    const map = new Map();
    (Array.isArray(usersList) ? usersList : []).forEach((user) => {
      if (user?.uid) map.set(user.uid, user);
    });
    const authUser = auth.currentUser;
    if (authUser?.uid && !map.has(authUser.uid)) {
      map.set(authUser.uid, {
        uid: authUser.uid,
        displayName: authUser.displayName || "",
        email: authUser.email || "",
        profileImage: authUser.photoURL || "",
      });
    }
    return map;
  }, [usersList]);

  useEffect(() => {
    if (!capsuleId || !canAccessThread) {
      setComments([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError("");

    const commentsQuery = query(
      collection(db, "timeCapsules", capsuleId, "comments"),
      orderBy("createdAt", "asc")
    );

    const unsub = onSnapshot(
      commentsQuery,
      (snap) => {
        const next = snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
        setComments(next);
        setLoading(false);
      },
      (snapshotError) => {
        console.error("Failed to load capsule thread:", snapshotError);
        setError("Unable to load discussion right now.");
        setLoading(false);
      }
    );

    return () => unsub();
  }, [capsuleId, canAccessThread]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport) return undefined;

    const viewport = window.visualViewport;
    const updateKeyboardInset = () => {
      const layoutHeight = window.innerHeight || 0;
      const visibleBottom = viewport.height + viewport.offsetTop;
      const nextInset = Math.max(0, Math.round(layoutHeight - visibleBottom));
      setKeyboardInset(nextInset);
    };

    updateKeyboardInset();
    viewport.addEventListener("resize", updateKeyboardInset);
    viewport.addEventListener("scroll", updateKeyboardInset);
    return () => {
      viewport.removeEventListener("resize", updateKeyboardInset);
      viewport.removeEventListener("scroll", updateKeyboardInset);
    };
  }, []);

  const rawDepthById = useMemo(() => buildRawDepthById(comments), [comments]);

  const commentTree = useMemo(
    () => buildCommentTree(comments, rawDepthById, MAX_NESTING_DEPTH),
    [comments, rawDepthById]
  );

  const commentById = useMemo(() => {
    const map = new Map();
    comments.forEach((comment) => {
      if (comment?.id) map.set(comment.id, comment);
    });
    return map;
  }, [comments]);

  const depthById = useMemo(() => {
    const map = new Map();

    const walk = (node, depth) => {
      if (!node?.id || map.has(node.id)) return;
      map.set(node.id, depth);
      const children = Array.isArray(node.children) ? node.children : [];
      children.forEach((child) => walk(child, depth + 1));
    };

    commentTree.forEach((root) => walk(root, 0));
    return map;
  }, [commentTree]);

  const resolveCappedReplyTarget = useCallback(
    (parentId) => {
      const parentNode = commentById.get(parentId);
      if (!parentNode) {
        return { parentId, replyToAuthorId: null, replyToAuthorHandle: "" };
      }

      const parentDepth = Number(depthById.get(parentId));
      if (!Number.isFinite(parentDepth) || parentDepth < MAX_NESTING_DEPTH) {
        return { parentId, replyToAuthorId: null, replyToAuthorHandle: "" };
      }

      let cursorId = parentId;
      let nextParentId = parentId;
      while (cursorId) {
        const cursorDepth = Number(depthById.get(cursorId));
        if (Number.isFinite(cursorDepth) && cursorDepth <= MAX_NESTING_DEPTH - 1) {
          nextParentId = cursorId;
          break;
        }
        const cursorNode = commentById.get(cursorId);
        const upstreamParent = cursorNode?.parentId || null;
        if (!upstreamParent) break;
        nextParentId = upstreamParent;
        cursorId = upstreamParent;
      }

      const targetUserRecord = userById.get(parentNode.authorId) || null;
      const replyToAuthorHandle = resolvePublicHandle(targetUserRecord, parentNode);
      return {
        parentId: nextParentId,
        replyToAuthorId: parentNode.authorId || null,
        replyToAuthorHandle,
      };
    },
    [commentById, depthById, userById]
  );

  const mentionCandidates = useMemo(() => {
    const byUid = new Map();
    (Array.isArray(usersList) ? usersList : []).forEach((user) => {
      const uid = String(user?.uid || "").trim();
      if (
        isMentionableUserId(uid, isPrivateCapsule, privateMentionParticipantIds) &&
        !byUid.has(uid)
      ) {
        byUid.set(uid, user);
      }
    });

    const authUser = auth.currentUser;
    const authUid = String(authUser?.uid || "").trim();
    if (
      isMentionableUserId(authUid, isPrivateCapsule, privateMentionParticipantIds) &&
      !byUid.has(authUid)
    ) {
      byUid.set(authUser.uid, {
        uid: authUser.uid,
        displayName: authUser.displayName || "",
        email: authUser.email || "",
        profileImage: authUser.photoURL || "",
      });
    }

    return Array.from(byUid.values())
      .map((user) => {
        const handle = resolvePublicHandle(user, null);
        if (!handle) return null;
        const displayName = resolveUserLabel(user);
        const searchText = [
          handle,
          String(user?.displayName || ""),
          String(user?.publicHandle || ""),
          String(user?.userName || ""),
          String(user?.email || ""),
        ]
          .join(" ")
          .toLowerCase();
        return {
          uid: String(user?.uid || "").trim(),
          handle,
          displayName,
          profileImage: String(user?.profileImage || "").trim(),
          searchText,
        };
      })
      .filter((entry) => entry?.uid && entry?.handle);
  }, [isPrivateCapsule, privateMentionParticipantIds, usersList]);

  const mentionCandidateByHandle = useMemo(() => {
    const map = new Map();
    mentionCandidates.forEach((entry) => {
      if (entry?.handle && !map.has(entry.handle)) map.set(entry.handle, entry);
    });
    return map;
  }, [mentionCandidates]);

  const submitComment = useCallback(
    async ({ text, parentId = null, replyToAuthorId = null, replyToAuthorHandle = "" }) => {
      const trimmed = String(text || "").trim();
      if (!trimmed || !capsuleId || !currentUserId || !canPostToThread) return;

      const currentUserRecord = userById.get(currentUserId) || {
        uid: currentUserId,
        displayName: auth.currentUser?.displayName || "",
        email: auth.currentUser?.email || "",
        profileImage: auth.currentUser?.photoURL || "",
      };

      const mentionedHandles = extractMentionHandles(trimmed);
      const mentionTargets = mentionedHandles
        .map((handle) => mentionCandidateByHandle.get(handle)?.uid || "")
        .filter((uid) => uid && uid !== currentUserId);

      const payload = {
        authorId: currentUserId,
        authorDisplayName: auth.currentUser?.displayName || "",
        authorEmail: auth.currentUser?.email || "",
        authorAvatar: auth.currentUser?.photoURL || "",
        authorPublicHandle: resolvePublicHandle(currentUserRecord, null),
        text: trimmed,
        parentId: parentId || null,
        replyToAuthorId: replyToAuthorId || null,
        replyToAuthorHandle: normalizeHandle(replyToAuthorHandle),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isEdited: false,
        isDeleted: false,
        voteScore: 1,
        mentionedHandles,
        mentionedUserIds: Array.from(new Set(mentionTargets)),
      };

      await addDoc(collection(db, "timeCapsules", capsuleId, "comments"), payload);
    },
    [capsuleId, currentUserId, canPostToThread, mentionCandidateByHandle, userById]
  );

  const handlePostTopLevel = useCallback(async () => {
    if (postingTopLevel) return;
    setPostingTopLevel(true);
    setError("");
    try {
      await submitComment({ text: newCommentText });
      setNewCommentText("");
    } catch (submitError) {
      console.error("Failed to post top-level comment:", submitError);
      setError("Failed to post comment. Please try again.");
    } finally {
      setPostingTopLevel(false);
    }
  }, [newCommentText, postingTopLevel, submitComment]);

  const handlePostReply = useCallback(
    async (parentId) => {
      if (!parentId || postingReplyById[parentId]) return;
      const text = replyDrafts[parentId] || "";
      const cappedTarget = resolveCappedReplyTarget(parentId);
      setPostingReplyById((prev) => ({ ...prev, [parentId]: true }));
      setError("");
      try {
        await submitComment({
          text,
          parentId: cappedTarget.parentId,
          replyToAuthorId: cappedTarget.replyToAuthorId,
          replyToAuthorHandle: cappedTarget.replyToAuthorHandle,
        });
        setReplyDrafts((prev) => ({ ...prev, [parentId]: "" }));
        setReplyOpenById((prev) => ({ ...prev, [parentId]: false }));
      } catch (submitError) {
        console.error("Failed to post reply:", submitError);
        setError("Failed to post reply. Please try again.");
      } finally {
        setPostingReplyById((prev) => ({ ...prev, [parentId]: false }));
      }
    },
    [postingReplyById, replyDrafts, resolveCappedReplyTarget, submitComment]
  );

  const handleStartEdit = useCallback((commentId, currentText) => {
    if (!commentId) return;
    setEditingById((prev) => ({ ...prev, [commentId]: true }));
    setEditDrafts((prev) => ({
      ...prev,
      [commentId]: typeof prev[commentId] === "string" ? prev[commentId] : String(currentText || ""),
    }));
  }, []);

  const handleCancelEdit = useCallback((commentId) => {
    if (!commentId) return;
    setEditingById((prev) => ({ ...prev, [commentId]: false }));
  }, []);

  const handleSaveEdit = useCallback(
    async (node) => {
      const commentId = node?.id;
      if (!commentId || node?.authorId !== currentUserId || node?.isDeleted) return;
      if (savingEditById[commentId]) return;

      const nextText = String(editDrafts[commentId] || "").trim();
      if (!nextText) {
        setError("Comment cannot be empty.");
        return;
      }
      if (nextText === String(node?.text || "").trim()) {
        setEditingById((prev) => ({ ...prev, [commentId]: false }));
        return;
      }

      setSavingEditById((prev) => ({ ...prev, [commentId]: true }));
      setError("");
      try {
        await updateDoc(doc(db, "timeCapsules", capsuleId, "comments", commentId), {
          text: nextText,
          mentionedHandles: extractMentionHandles(nextText),
          mentionedUserIds: Array.from(
            new Set(
              extractMentionHandles(nextText)
                .map((handle) => mentionCandidateByHandle.get(handle)?.uid || "")
                .filter((uid) => uid && uid !== currentUserId)
            )
          ),
          isEdited: true,
          editedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        setEditingById((prev) => ({ ...prev, [commentId]: false }));
      } catch (saveError) {
        console.error("Failed to edit comment:", saveError);
        setError("Failed to edit comment. Please try again.");
      } finally {
        setSavingEditById((prev) => ({ ...prev, [commentId]: false }));
      }
    },
    [capsuleId, currentUserId, editDrafts, mentionCandidateByHandle, savingEditById]
  );

  const handleRequestDelete = useCallback(
    (node) => {
      const commentId = node?.id;
      if (!commentId || node?.authorId !== currentUserId || node?.isDeleted) return;
      if (deletingById[commentId]) return;
      setDeleteTargetNode(node);
    },
    [currentUserId, deletingById]
  );

  const handleCancelDelete = useCallback(() => {
    setDeleteTargetNode(null);
  }, []);

  const handleSoftDelete = useCallback(
    async (node) => {
      const commentId = node?.id;
      if (!commentId || node?.authorId !== currentUserId || node?.isDeleted) return;
      if (deletingById[commentId]) return;

      setDeletingById((prev) => ({ ...prev, [commentId]: true }));
      setError("");
      try {
        await updateDoc(doc(db, "timeCapsules", capsuleId, "comments", commentId), {
          text: "",
          isDeleted: true,
          deletedAt: serverTimestamp(),
          deletedBy: currentUserId,
          updatedAt: serverTimestamp(),
        });
        setEditingById((prev) => ({ ...prev, [commentId]: false }));
        setDeleteTargetNode(null);
      } catch (deleteError) {
        console.error("Failed to delete comment:", deleteError);
        setError("Failed to delete comment. Please try again.");
      } finally {
        setDeletingById((prev) => ({ ...prev, [commentId]: false }));
      }
    },
    [capsuleId, currentUserId, deletingById]
  );

  const handleVote = useCallback(
    async (node, nextVote) => {
      const commentId = String(node?.id || "").trim();
      if (!commentId || !currentUserId || !canAccessThread) return;
      if (![1, -1].includes(nextVote)) return;
      if (votingById[commentId]) return;

      setVotingById((prev) => ({ ...prev, [commentId]: true }));
      try {
        await runTransaction(db, async (tx) => {
          const commentRef = doc(db, "timeCapsules", capsuleId, "comments", commentId);
          const snap = await tx.get(commentRef);
          if (!snap.exists()) return;

          const data = snap.data() || {};
          const votesByUser =
            data.commentVotesByUser && typeof data.commentVotesByUser === "object"
              ? data.commentVotesByUser
              : {};
          const previousVote = Number(votesByUser[currentUserId] || 0);
          const normalizedPrev = previousVote === 1 || previousVote === -1 ? previousVote : 0;
          const normalizedNext = normalizedPrev === nextVote ? 0 : nextVote;
          const currentScore = Number(data.voteScore);
          const safeScore = Number.isFinite(currentScore) ? currentScore : 1;
          const nextScore = safeScore - normalizedPrev + normalizedNext;

          const voteFieldPath = `commentVotesByUser.${currentUserId}`;
          const patch = {
            voteScore: nextScore,
            updatedAt: serverTimestamp(),
          };
          if (normalizedNext === 0) {
            patch[voteFieldPath] = deleteField();
          } else {
            patch[voteFieldPath] = normalizedNext;
          }

          tx.update(commentRef, patch);
        });
      } catch (voteError) {
        console.error("Failed to vote on comment:", voteError);
      } finally {
        setVotingById((prev) => ({ ...prev, [commentId]: false }));
      }
    },
    [canAccessThread, capsuleId, currentUserId, votingById]
  );

  const threadColors = useMemo(() => {
    return {
      panelBg: theme.palette.background.paper,
      panelBorder: theme.palette.divider,
      title: theme.palette.text.primary,
      subtitle: theme.palette.text.secondary,
      text: theme.palette.text.primary,
      dim: theme.palette.text.secondary,
      line: theme.palette.divider,
      action: theme.colors.blue,
      senderBadgeBg:
        theme.palette.mode === "dark"
          ? alpha(theme.colors.blue, 0.2)
          : alpha(theme.colors.blue, 0.1),
      senderBadgeText: theme.colors.blue,
      inputBg:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.text.primary, 0.04)
          : alpha(theme.colors.blue, 0.04),
      inputBorder: theme.palette.divider,
      buttonBg: theme.colors.blue,
      inputHoverBorder: theme.palette.text.secondary,
      inputFocusBorder: theme.colors.blue,
      buttonHoverBg: theme.colors.darkBlue,
      avatarBg:
        theme.palette.mode === "dark"
          ? alpha(theme.colors.blue, 0.25)
          : alpha(theme.colors.blue, 0.12),
      avatarFg: theme.palette.mode === "dark" ? theme.palette.text.primary : theme.colors.blue,
    };
  }, [theme]);

  const visibleMentionSuggestions = useMemo(() => {
    if (!mentionContext) return [];
    const q = String(mentionContext.query || "").trim().toLowerCase();
    const results = mentionCandidates.filter((entry) => {
      if (!entry?.uid || entry.uid === currentUserId) return false;
      if (!q) return true;
      return entry.handle.includes(q) || entry.searchText.includes(q);
    });
    return results.slice(0, 6);
  }, [currentUserId, mentionCandidates, mentionContext]);
  const shouldUseMentionSheet = isMobileViewport || keyboardInset > 0;

  const updateMentionState = useCallback((scopeKey, text, caret) => {
    const ctx = getMentionContext(text, caret);
    if (!ctx) {
      setMentionContext((prev) => (prev?.scopeKey === scopeKey ? null : prev));
      return;
    }
    setMentionContext({
      ...ctx,
      scopeKey,
    });
  }, []);

  const applyMentionSelection = useCallback(
    (candidate) => {
      if (!candidate?.handle || !mentionContext?.scopeKey) return;
      const insertHandle = `@${candidate.handle}`;
      const scopeKey = mentionContext.scopeKey;

      const applyToText = (text) => {
        const raw = String(text || "");
        const before = raw.slice(0, mentionContext.start);
        const after = raw.slice(mentionContext.end);
        const insertText = `${insertHandle} `;
        const nextValue = `${before}${insertText}${after}`;
        const cursorPos = before.length + insertText.length;
        return { nextValue, cursorPos };
      };

      const focusInput = (input, cursorPos) => {
        if (!input) return;
        try {
          input.focus();
          if (typeof input.setSelectionRange === "function") {
            input.setSelectionRange(cursorPos, cursorPos);
          }
        } catch (err) {
          console.warn("Failed to set mention cursor position:", err);
        }
      };

      if (scopeKey === "top") {
        const { nextValue, cursorPos } = applyToText(newCommentText);
        setNewCommentText(nextValue);
        setTimeout(() => focusInput(topLevelInputRef.current, cursorPos), 0);
      } else if (scopeKey.startsWith("reply:")) {
        const replyId = scopeKey.slice("reply:".length);
        const currentText = replyDrafts[replyId] || "";
        const { nextValue, cursorPos } = applyToText(currentText);
        setReplyDrafts((prev) => ({ ...prev, [replyId]: nextValue }));
        setTimeout(() => focusInput(replyInputRefs.current[replyId], cursorPos), 0);
      }

      setMentionContext(null);
    },
    [mentionContext, newCommentText, replyDrafts]
  );

  const renderMentionSuggestions = useCallback(
    (scopeKey) => {
      if (!mentionContext || mentionContext.scopeKey !== scopeKey) return null;
      const isSheet = shouldUseMentionSheet;

      return (
        <Box
          sx={{
            ...(isSheet
              ? {
                  position: "fixed",
                  left: 12,
                  right: 12,
                  bottom:
                    keyboardInset > 0
                      ? `calc(${keyboardInset}px + 12px)`
                      : "calc(env(safe-area-inset-bottom, 0px) + 84px)",
                  zIndex: 1500,
                  maxHeight: "min(260px, 42dvh)",
                  overflowY: "auto",
                  boxShadow: "0 18px 44px rgba(0,0,0,0.32)",
                  WebkitOverflowScrolling: "touch",
                }
              : {}),
            border: "1px solid",
            borderColor: threadColors.inputBorder,
            borderRadius: "12px",
            overflow: "hidden",
            backgroundColor: threadColors.panelBg,
          }}
        >
          {visibleMentionSuggestions.length ? visibleMentionSuggestions.map((entry) => (
            <Button
              key={`${scopeKey}:${entry.uid}`}
              onClick={() => applyMentionSelection(entry)}
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-start",
                textTransform: "none",
                borderRadius: 0,
                px: 1,
                py: 0.6,
                color: threadColors.text,
                gap: 1,
              }}
            >
              <Avatar src={entry.profileImage || undefined} sx={{ width: 22, height: 22, fontSize: 11 }}>
                {!entry.profileImage ? getInitials(entry.displayName) : null}
              </Avatar>
              <Box sx={{ textAlign: "left", minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: threadColors.text }}>
                  @{entry.handle}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: threadColors.dim, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  {entry.displayName}
                </Typography>
              </Box>
            </Button>
          )) : (
            <Box sx={{ px: 1.25, py: 1, color: threadColors.dim }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                No matching people
              </Typography>
              <Typography variant="caption">
                Try another name or handle.
              </Typography>
            </Box>
          )}
        </Box>
      );
    },
    [applyMentionSelection, keyboardInset, mentionContext, shouldUseMentionSheet, threadColors, visibleMentionSuggestions]
  );

  const renderCommentTextWithMentionLinks = useCallback(
    (node) => {
      const rawText = String(node?.text || "");
      if (!rawText) return "";

      const handleToUid = new Map();
      const storedHandles = Array.isArray(node?.mentionedHandles) ? node.mentionedHandles : [];
      const storedUserIds = Array.isArray(node?.mentionedUserIds) ? node.mentionedUserIds : [];
      for (let i = 0; i < storedHandles.length; i += 1) {
        const handle = normalizeHandle(storedHandles[i]);
        const uid = String(storedUserIds[i] || "").trim();
        if (handle && uid && !handleToUid.has(handle)) {
          handleToUid.set(handle, uid);
        }
      }

      const parts = [];
      const regex = /(^|\s)@([a-zA-Z0-9._]{1,30})/g;
      let lastIndex = 0;
      let match = regex.exec(rawText);
      while (match) {
        const leading = match[1] || "";
        const rawHandle = match[2] || "";
        const mentionStart = match.index + leading.length;
        const mentionEnd = mentionStart + 1 + rawHandle.length;
        const normalizedHandle = normalizeHandle(rawHandle);

        if (mentionStart > lastIndex) {
          parts.push(rawText.slice(lastIndex, mentionStart));
        }

        if (leading) {
          parts.push(leading);
        }

        const linkedUid =
          handleToUid.get(normalizedHandle) ||
          mentionCandidateByHandle.get(normalizedHandle)?.uid ||
          "";

        if (linkedUid) {
          parts.push(
            <Box
              key={`mention:${node?.id || "node"}:${mentionStart}:${normalizedHandle}`}
              component={RouterLink}
              to={getProfilePathByUid(linkedUid)}
              sx={{
                color: threadColors.action,
                fontWeight: 700,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              @{normalizedHandle}
            </Box>
          );
        } else {
          parts.push(`@${rawHandle}`);
        }

        lastIndex = mentionEnd;
        if (regex.lastIndex < mentionEnd) {
          regex.lastIndex = mentionEnd;
        }
        match = regex.exec(rawText);
      }

      if (lastIndex < rawText.length) {
        parts.push(rawText.slice(lastIndex));
      }

      return parts;
    },
    [mentionCandidateByHandle, threadColors.action]
  );

  if (!canAccessThread) return null;

  const renderNode = (node, depth = 0) => {
    const children = Array.isArray(node.children) ? node.children : [];
    const hasChildren = children.length > 0;
    const isCollapsed = Boolean(collapsedById[node.id]);
    const isReplyOpen = Boolean(replyOpenById[node.id]);
    const isEditing = Boolean(editingById[node.id]);
    const isSavingEdit = Boolean(savingEditById[node.id]);
    const isDeleting = Boolean(deletingById[node.id]);
    const isVoting = Boolean(votingById[node.id]);
    const isOwnComment = Boolean(currentUserId && node.authorId === currentUserId);
    const isDeleted = node?.isDeleted === true;
    const currentVoteRaw = Number(node?.commentVotesByUser?.[currentUserId] || 0);
    const currentVote = currentVoteRaw === 1 || currentVoteRaw === -1 ? currentVoteRaw : 0;
    const voteScoreRaw = Number(node?.voteScore);
    const voteScore = Number.isFinite(voteScoreRaw) ? voteScoreRaw : 1;
    const isHiddenByScore = !isDeleted && voteScore <= HIDE_SCORE_THRESHOLD;
    const showHidden = Boolean(showHiddenById[node.id]);
    const shouldCollapseByScore = isHiddenByScore && !showHidden;
    const userRecord =
      userById.get(node.authorId) ||
      (node.authorId === currentUserId
        ? {
            uid: currentUserId,
            displayName: auth.currentUser?.displayName || "",
            email: auth.currentUser?.email || "",
            profileImage: auth.currentUser?.photoURL || "",
          }
        : {
            uid: node.authorId || "",
            displayName: node.authorDisplayName || "",
            email: node.authorEmail || "",
            profileImage: node.authorAvatar || "",
          });
    const authorName = resolveUserLabel(userRecord);
    const isSender = node.authorId && node.authorId === capsule?.senderId;
    const storedReplyToHandle = normalizeHandle(node?.replyToAuthorHandle || "");
    const legacyParentNode = node?.parentId ? commentById.get(node.parentId) : null;
    const legacyReplyToHandle =
      !storedReplyToHandle && Number(rawDepthById.get(node.id)) > MAX_NESTING_DEPTH
        ? resolvePublicHandle(userById.get(legacyParentNode?.authorId) || null, legacyParentNode)
        : "";
    const replyToHandle = storedReplyToHandle || legacyReplyToHandle;
    const replyToUserId =
      String(node?.replyToAuthorId || "").trim() ||
      String(mentionCandidateByHandle.get(replyToHandle)?.uid || "").trim();

    return (
      <Box key={node.id} sx={{ mt: depth === 0 ? 1.25 : 0.9, pl: depth ? 1.1 : 0 }}>
        <Box
          sx={{
            borderLeft: depth ? "2px solid" : "none",
            borderColor: threadColors.line,
            pl: depth ? 1.1 : 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={() =>
                  setCollapsedById((prev) => ({ ...prev, [node.id]: !Boolean(prev[node.id]) }))
                }
                sx={{ mt: 0.05, color: threadColors.dim, p: 0.25 }}
              >
                {isCollapsed ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            ) : (
              <Box sx={{ width: 24 }} />
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Box
                  component={node.authorId ? RouterLink : "div"}
                  to={node.authorId ? getProfilePathByUid(node.authorId) : undefined}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: 0,
                    textDecoration: "none",
                    color: "inherit",
                  }}
                >
                  <Avatar
                    src={userRecord?.profileImage || undefined}
                    sx={{
                      width: 22,
                      height: 22,
                      fontSize: 10,
                      bgcolor: threadColors.avatarBg,
                      color: threadColors.avatarFg,
                    }}
                  >
                    {userRecord?.profileImage ? null : getInitials(authorName)}
                  </Avatar>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: threadColors.title,
                      fontSize: 13,
                      whiteSpace: "normal",
                      overflow: "visible",
                      textOverflow: "clip",
                      wordBreak: "break-word",
                    }}
                  >
                    {authorName}
                  </Typography>
                </Box>
                {isSender && (
                  <Box
                    sx={{
                      px: 0.75,
                      py: 0.1,
                      borderRadius: "999px",
                      bgcolor: threadColors.senderBadgeBg,
                      color: threadColors.senderBadgeText,
                      fontSize: 10,
                      fontWeight: 700,
                      lineHeight: 1.5,
                    }}
                  >
                    OP
                  </Box>
                )}
                <Typography variant="caption" sx={{ color: threadColors.dim, fontSize: 11 }}>
                  {formatTimestamp(node.createdAt)}
                </Typography>
                {!isDeleted && node?.isEdited === true && (
                  <Typography variant="caption" sx={{ color: threadColors.dim, fontSize: 11 }}>
                    (edited)
                  </Typography>
                )}
              </Box>

              {isEditing && !isDeleted ? (
                <Box sx={{ mt: 0.7, display: "grid", gap: 0.8 }}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    value={editDrafts[node.id] || ""}
                    onChange={(event) =>
                      setEditDrafts((prev) => ({ ...prev, [node.id]: event.target.value }))
                    }
                    inputProps={{ maxLength: COMMENT_LIMIT }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "16px",
                        backgroundColor: threadColors.inputBg,
                        color: threadColors.text,
                        "& fieldset": { borderColor: threadColors.inputBorder },
                        "&:hover fieldset": { borderColor: threadColors.inputHoverBorder },
                        "&.Mui-focused fieldset": { borderColor: threadColors.inputFocusBorder },
                      },
                    }}
                  />
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="caption" sx={{ color: threadColors.dim }}>
                      {(editDrafts[node.id] || "").length}/{COMMENT_LIMIT}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.7 }}>
                      <Button
                        size="small"
                        startIcon={<CloseIcon fontSize="small" />}
                        onClick={() => handleCancelEdit(node.id)}
                        disabled={isSavingEdit}
                        sx={{ textTransform: "none", color: threadColors.dim, minWidth: 0, px: 0.25 }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        startIcon={<CheckIcon fontSize="small" />}
                        onClick={() => handleSaveEdit(node)}
                        disabled={!String(editDrafts[node.id] || "").trim() || isSavingEdit}
                        sx={{ textTransform: "none", color: threadColors.action, minWidth: 0, px: 0.25 }}
                      >
                        {isSavingEdit ? "Saving..." : "Save"}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.45,
                    color: isDeleted ? threadColors.dim : threadColors.text,
                    fontStyle: isDeleted ? "italic" : "normal",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    lineHeight: 1.5,
                  }}
                >
                  {isDeleted ? (
                    "Comment deleted"
                  ) : shouldCollapseByScore ? (
                    <>
                      <Typography component="span" sx={{ fontStyle: "italic", color: threadColors.dim }}>
                        Comment hidden due to low score.
                      </Typography>{" "}
                      <Button
                        size="small"
                        onClick={() =>
                          setShowHiddenById((prev) => ({ ...prev, [node.id]: true }))
                        }
                        sx={{
                          textTransform: "none",
                          minWidth: 0,
                          px: 0.25,
                          color: threadColors.action,
                          fontWeight: 700,
                          fontSize: 12,
                          verticalAlign: "baseline",
                        }}
                      >
                        Show anyway
                      </Button>
                    </>
                  ) : (
                    <>
                      {replyToHandle ? (
                        <Box
                          component={replyToUserId ? RouterLink : "span"}
                          to={replyToUserId ? getProfilePathByUid(replyToUserId) : undefined}
                          sx={{ color: threadColors.action, fontWeight: 700, mr: 0.5 }}
                        >
                          @{replyToHandle}
                        </Box>
                      ) : null}
                      {renderCommentTextWithMentionLinks(node)}
                    </>
                  )}
                </Typography>
              )}

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.45 }}>
                {!isDeleted && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.2 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleVote(node, 1)}
                      disabled={!canPostToThread || isVoting}
                      sx={{
                        color: currentVote === 1 ? threadColors.action : threadColors.dim,
                        p: 0.25,
                      }}
                      aria-label="Upvote comment"
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <Typography
                      variant="caption"
                      sx={{
                        color: voteScore > 0 ? threadColors.action : threadColors.dim,
                        minWidth: 18,
                        textAlign: "center",
                        fontWeight: 700,
                      }}
                    >
                      {voteScore}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleVote(node, -1)}
                      disabled={!canPostToThread || isVoting}
                      sx={{
                        color: currentVote === -1 ? "error.main" : threadColors.dim,
                        p: 0.25,
                      }}
                      aria-label="Downvote comment"
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
                {!isDeleted && !shouldCollapseByScore && canPostToThread && (
                  <Button
                    size="small"
                    startIcon={<ReplyIcon fontSize="small" />}
                    onClick={() =>
                      setReplyOpenById((prev) => ({ ...prev, [node.id]: !Boolean(prev[node.id]) }))
                    }
                    sx={{
                      textTransform: "none",
                      minWidth: 0,
                      px: 0.25,
                      color: threadColors.action,
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    Reply
                  </Button>
                )}
                {isOwnComment && !isDeleted && !isEditing && !shouldCollapseByScore && (
                  <Button
                    size="small"
                    startIcon={<EditOutlinedIcon fontSize="small" />}
                    onClick={() => handleStartEdit(node.id, node.text)}
                    sx={{
                      textTransform: "none",
                      minWidth: 0,
                      px: 0.25,
                      color: threadColors.dim,
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    Edit
                  </Button>
                )}
                {isOwnComment && !isDeleted && !shouldCollapseByScore && (
                  <Button
                    size="small"
                    startIcon={<DeleteOutlineOutlinedIcon fontSize="small" />}
                    onClick={() => handleRequestDelete(node)}
                    disabled={isDeleting}
                    sx={{
                      textTransform: "none",
                      minWidth: 0,
                      px: 0.25,
                      color: threadColors.dim,
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    {isDeleting ? "Deleting..." : "Delete"}
                  </Button>
                )}
                {hasChildren && (
                  <Button
                    size="small"
                    onClick={() =>
                      setCollapsedById((prev) => ({ ...prev, [node.id]: !Boolean(prev[node.id]) }))
                    }
                    sx={{
                      textTransform: "none",
                      minWidth: 0,
                      px: 0.25,
                      color: threadColors.dim,
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    {isCollapsed ? `Expand (${children.length})` : "Collapse"}
                  </Button>
                )}
              </Box>

              {isReplyOpen && !isDeleted && !isEditing && !shouldCollapseByScore && canPostToThread && (
                <Box sx={{ mt: 0.8, display: "grid", gap: 0.8 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Add a reply..."
                    multiline
                    minRows={2}
                    value={replyDrafts[node.id] || ""}
                    inputRef={(el) => {
                      if (!el) return;
                      replyInputRefs.current[node.id] = el;
                    }}
                    onChange={(event) =>
                      {
                        const nextValue = event.target.value;
                        setReplyDrafts((prev) => ({ ...prev, [node.id]: nextValue }));
                        updateMentionState(`reply:${node.id}`, nextValue, event.target.selectionStart);
                      }
                    }
                    inputProps={{ maxLength: COMMENT_LIMIT }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "16px",
                        backgroundColor: threadColors.inputBg,
                        color: threadColors.text,
                        "& fieldset": { borderColor: threadColors.inputBorder },
                        "&:hover fieldset": { borderColor: threadColors.inputHoverBorder },
                        "&.Mui-focused fieldset": { borderColor: threadColors.inputFocusBorder },
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: threadColors.dim,
                        opacity: 1,
                      },
                    }}
                  />
                  {renderMentionSuggestions(`reply:${node.id}`)}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="caption" sx={{ color: threadColors.dim }}>
                      {(replyDrafts[node.id] || "").length}/{COMMENT_LIMIT}
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handlePostReply(node.id)}
                      disabled={!String(replyDrafts[node.id] || "").trim() || postingReplyById[node.id]}
                      sx={{
                        textTransform: "none",
                        borderRadius: "999px",
                        boxShadow: "none",
                        bgcolor: threadColors.buttonBg,
                        fontWeight: 700,
                        "&:hover": { boxShadow: "none", bgcolor: threadColors.buttonHoverBg },
                      }}
                    >
                      {postingReplyById[node.id] ? "Posting..." : "Post reply"}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {!isCollapsed && children.map((child) => renderNode(child, depth + 1))}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        mt: 3,
        border: "1px solid",
        borderColor: threadColors.panelBorder,
        backgroundColor: threadColors.panelBg,
        borderRadius: 1,
        p: 1.4,
      }}
    >
      <Typography variant="h6" sx={{ color: threadColors.title, fontSize: 20, mb: 0.2 }}>
        Discussion
      </Typography>
      <Typography variant="caption" sx={{ color: threadColors.subtitle, display: "block", mb: 1.3 }}>
        {isPrivateCapsule
          ? "Sender and recipients only."
          : canPostToThread
            ? "Public discussion."
            : "Public discussion. Sign in to join."}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1.25 }}>
          {error}
        </Alert>
      )}

      {!canPostToThread && (
        <Alert severity="info" sx={{ mb: 1.25 }}>
          Sign in to post comments.
        </Alert>
      )}

      <Box sx={{ display: "grid", gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Join the conversation"
          multiline
          minRows={1}
          maxRows={4}
          inputRef={topLevelInputRef}
          value={newCommentText}
          onChange={(event) => {
            const nextValue = event.target.value;
            setNewCommentText(nextValue);
            updateMentionState("top", nextValue, event.target.selectionStart);
          }}
          disabled={!canPostToThread}
          inputProps={{ maxLength: COMMENT_LIMIT }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "999px",
              backgroundColor: threadColors.inputBg,
              color: threadColors.text,
              "& fieldset": { borderColor: threadColors.inputBorder },
              "&:hover fieldset": { borderColor: threadColors.inputHoverBorder },
              "&.Mui-focused fieldset": { borderColor: threadColors.inputFocusBorder },
            },
            "& .MuiInputBase-input::placeholder": {
              color: threadColors.dim,
              opacity: 1,
            },
          }}
        />
        {renderMentionSuggestions("top")}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="caption" sx={{ color: threadColors.dim }}>
            {newCommentText.length}/{COMMENT_LIMIT}
          </Typography>
          <Button
            variant="contained"
            onClick={handlePostTopLevel}
            disabled={!canPostToThread || !newCommentText.trim() || postingTopLevel}
            sx={{
              textTransform: "none",
              borderRadius: "999px",
              boxShadow: "none",
              bgcolor: threadColors.buttonBg,
              fontWeight: 700,
              "&:hover": { boxShadow: "none", bgcolor: threadColors.buttonHoverBg },
            }}
          >
            {postingTopLevel ? "Posting..." : "Post comment"}
          </Button>
        </Box>
      </Box>

      <Box sx={{ mt: 1.5 }}>
        {loading ? (
          <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={20} sx={(t) => ({ color: t.colors.blue })} />
          </Box>
        ) : commentTree.length > 0 ? (
          commentTree.map((node) => renderNode(node))
        ) : (
          <Typography variant="body2" sx={{ py: 1, color: threadColors.dim }}>
            No comments yet. Start the thread.
          </Typography>
        )}
      </Box>

      <Dialog open={Boolean(deleteTargetNode)} onClose={handleCancelDelete} fullWidth maxWidth="xs">
        <DialogTitle>Delete comment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Replies will remain visible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => handleSoftDelete(deleteTargetNode)}
            disabled={Boolean(deleteTargetNode?.id && deletingById[deleteTargetNode.id])}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
